/*global gapi*/
import {googleApiInitializer} from '../helpers/social'
import config from 'config'
import errorCodes from 'helpers/enums/errorCodes'
import nearpodMetricsHelper from 'sync/helpers/nearpodMetricsHelper'

const initializeGapiAuth = (() => {
	let promise
	return () => {
		if (!promise) {
			const API_KEY = config.googleDrive.developerKey
			const CLIENT_ID = config.googleDrive.clientId
			const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest', 'https://slides.googleapis.com/$discovery/rest?version=v1']
			const SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.install https://www.googleapis.com/auth/presentations'
			promise = gapi.client.init({
				apiKey: API_KEY,
				clientId: CLIENT_ID,
				discoveryDocs: DISCOVERY_DOCS,
				scope: SCOPES,
				cookiepolicy: 'none',
			})
		}
		return promise
	}
})()

function createFile() {

	return new Promise((resolve, reject) => {

		function handleCreationResponse(response) {
			switch (response.status) {
				case 200:
					const file = response.result
					resolve(file.id)
					break
				default:
					reject(response)
					break
			}
		}
		var body = {name: config.googleSlides.lessonName}
		gapi.client.drive.files.copy({
			fileId: config.googleSlides.fileId,
			resource: body,
		})
			.then(handleCreationResponse)
			.catch(handleCreationResponse)
	})
}

function getFileData(fileId) {
	return new Promise((resolve, reject) => {
		gapi.client.drive.files.get({
			fileId,
			fields: 'webViewLink,id,name',
		}).then(function(response) {
			switch (response.status) {
				case 200:
					resolve(response.result)
					break
				default:
					reject(response)
					break
			}
		}).catch(function(response) {
			reject(response)
		})
	})
}

function getFileSlides(presentationId) {
	return new Promise((resolve, reject) => {
		gapi.client.slides.presentations.get({
			presentationId,
		}).then(function(response) {
			const presentation = response.result
			resolve(presentation)
		}).catch(function(response) {
			reject(response)
		})
	})
}

const autorizeUser = (() => {
	let promise
	return () => {
		if (!promise) {
			promise = new Promise((resolve, reject) => {
				function updateSigninStatus(isSignedIn) {
					if (isSignedIn) {
						resolve()
					} else {
						gapi.auth2.getAuthInstance().signIn()
							.catch(e => {
								promise = null
								const error = formatAuthError(e)
								reject(error)
							})
					}
				}

				gapi.auth2.getAuthInstance().isSignedIn.listen(updateSigninStatus)
				updateSigninStatus(gapi.auth2.getAuthInstance().isSignedIn.get())
			})
		}
		return promise
	}
})()

const createGoogleSlideTemplate = async () => {
	try {
		await googleApiInitializer()
		await initializeGapiAuth()
		await autorizeUser()
		const fileId = await createFile()
		const fileData = await getFileData(fileId)
		return fileData
	} catch (error) {
		const nearpodError = formatGapiError(error, 'Unknown Error at Create Template')
		throw nearpodError
	}
}

const getGoogleSlideTitle = async fileId => {
	try {
		await googleApiInitializer()
		await initializeGapiAuth()
		await autorizeUser()
		const fileData = await getFileData(fileId)
		return fileData.name
	} catch (error) {
		return ''
	}
}

function getSlideThumbnailFromSlides(presentationId, pageObjectId) {
	return new Promise((resolve, reject) => {
		gapi.client.slides.presentations.pages.getThumbnail({
			presentationId,
			pageObjectId,
		}).then(function(response) {
			resolve(response.result)
		}).catch(function(error) {
			reject(error)
		})
	})
}

const getSlideThumbnail = async (presentationId, pageObjectId) => {
	try {
		await googleApiInitializer()
		await initializeGapiAuth()
		await autorizeUser()
		return await getSlideThumbnailFromSlides(presentationId, pageObjectId)
	} catch (error) {
		const nearpodError = formatGapiError(error, 'Unknown Error at Get Thumbnail')
		throw nearpodError
	}
}

const getGoogleSlideSlides = async presentationId => {
	try {
		await googleApiInitializer()
		await initializeGapiAuth()
		await autorizeUser()
		return await getFileSlides(presentationId)
	} catch (error) {
		const nearpodError = formatGapiError(error, 'Unknown Error at Get Slides')
		throw nearpodError
	}
}

const formatErrorMessage = (googleError, defaultMessage) => {
	let message = defaultMessage

	if (googleError && googleError.details) {
		message = googleError.details
	} else if (googleError && googleError.error && typeof googleError.error === 'string') {
		message = googleError.error
	} else if (googleError && googleError.result && googleError.result.error) {
		message = googleError.result.error.message
	}

	return message
}

const formatErrorCode = error => {
	if (error && error.result && error.result.error) {
		if (error.result.error.code === 403 && error.result.error.status === 'PERMISSION_DENIED') {
			//both permission and limit errors have the same google error code,
			//we want to differentiate errors
			return errorCodes.GOOGLE_SLIDES_INVALID_PERMISSIONS_ERROR
		}

		return error.result.error.code
	}

	return -1
}

const formatGapiError = (error, defaultMessage = 'Unknown Error') => {
	const nearpodError = new Error()
	nearpodError.serverError = {
		error_code: formatErrorCode(error),
		message: formatErrorMessage(error, defaultMessage),
	}

	return nearpodError
}

const formatAuthError = response => {
	let code = -1
	if (response.error === 'popup_blocked_by_browser') {
		code = errorCodes.GOOGLE_SLIDES_POP_UP_BLOCKED
	}
	const error = {
		result: {
			error: {
				message: response.error,
				code,
			},
		},
	}

	return error
}

const googleUserSignIn = async () => {
	try {
		nearpodMetricsHelper.add({state: 'googleUserSignIn:init'})
		await googleApiInitializer()
		nearpodMetricsHelper.add({state: 'googleUserSignIn:googleApiInitializer_success'})
		await initializeGapiAuth()
		nearpodMetricsHelper.add({state: 'googleUserSignIn:initializeGapiAuth_success'})
		await autorizeUser()
		nearpodMetricsHelper.add({state: 'googleUserSignIn:autorizeUser_success'})
	} catch (error) {
		const nearpodError = formatGapiError(error, 'Unknown Error at User Sign In')
		throw nearpodError
	}
}

export {
	createGoogleSlideTemplate,
	getGoogleSlideTitle,
	getSlideThumbnail,
	getGoogleSlideSlides,
	autorizeUser,
	formatGapiError,
	googleUserSignIn,
}
